import jsPDF from 'jspdf';

const getStatusBadgeInfo = (ticket) => {
  if (ticket.bIsCombinado) {
    return {
      label: 'Combinado',
      bg: [243, 232, 255],
      border: [216, 180, 254],
      text: [107, 33, 168]
    };
  }

  switch (Number(ticket.iEstadoTicket)) {
    case 1:
      return {
        label: 'Pendiente',
        bg: [254, 243, 199],
        border: [252, 211, 77],
        text: [180, 83, 9]
      };
    case 3:
      return {
        label: 'En Progreso',
        bg: [219, 234, 254],
        border: [147, 197, 253],
        text: [29, 78, 216]
      };
    case 2:
      return {
        label: 'Resuelto',
        bg: [209, 250, 229],
        border: [110, 231, 183],
        text: [4, 120, 87]
      };
    default:
      return {
        label: 'Desconocido',
        bg: [243, 244, 246],
        border: [209, 213, 219],
        text: [75, 85, 99]
      };
  }
};

/**
 * Genera un reporte ejecutivo en PDF con diseño corporativo limpio.
 */
export const exportTicketsPdf = ({
  tickets = [],
  statusFilter = 'todos',
  selectedAgentId = '',
  dateFilter = {},
  agents = []
}) => {
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'letter'
  });

  const pageWidth = 279.4;
  const pageHeight = 215.9;
  const marginLeft = 14;
  const marginRight = 14;
  const contentWidth = pageWidth - marginLeft - marginRight; // 251.4 mm

  // Formatear etiquetas de filtros
  const selectedAgentObj = agents.find(a => String(a.idUsuario || a.id) === String(selectedAgentId));
  const agentLabel = selectedAgentObj
    ? (selectedAgentObj.vNombre || selectedAgentObj.nombre || selectedAgentObj.vNombreCompleto || `ID ${selectedAgentId}`)
    : (selectedAgentId ? `ID ${selectedAgentId}` : 'Todos los asesores');

  let statusLabel = 'Todos los estados';
  if (statusFilter === '1') statusLabel = 'Pendientes';
  else if (statusFilter === '3') statusLabel = 'En Progreso';
  else if (statusFilter === '2') statusLabel = 'Resueltos';
  else if (statusFilter === 'combinados') statusLabel = 'Combinados';

  let dateRangeLabel = 'Todas las fechas';
  if (dateFilter.dFechaInicio && dateFilter.dFechaFin) {
    dateRangeLabel = `${dateFilter.dFechaInicio} al ${dateFilter.dFechaFin}`;
  } else if (dateFilter.dFechaInicio) {
    dateRangeLabel = `Desde ${dateFilter.dFechaInicio}`;
  } else if (dateFilter.dFechaFin) {
    dateRangeLabel = `Hasta ${dateFilter.dFechaFin}`;
  }

  const currentDateStr = new Date().toLocaleDateString('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  // Anchos de columna optimizados (Suma = 251.4 mm)
  const colWidths = {
    ticket: 16,
    fecha: 26,
    cliente: 44,
    telefono: 24,
    asunto: 60,
    asesor: 40,
    estatus: 24,
    tiempo: 17.4
  };

  // Encabezado principal de la primera página
  const drawMainHeader = () => {
    // Línea de acento institucional superior
    doc.setFillColor(2, 132, 199); // Brand Sky-600 #0284c7
    doc.rect(0, 0, pageWidth, 3.5, 'F');

    // Título principal
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(15, 23, 42); // Slate-900
    doc.text('Reporte de Tickets de Soporte', marginLeft, 14);

    // Subtítulo
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(100, 116, 139); // Slate-500
    doc.text('Akasia Telecomunicaciones — Sistema Support Engine', marginLeft, 19);

    // Fecha de emisión y total a la derecha
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(`Fecha de emisión: ${currentDateStr}`, pageWidth - marginRight, 13, { align: 'right' });
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text(`Total tickets listados: ${tickets.length}`, pageWidth - marginRight, 18, { align: 'right' });

    // Barra ejecutiva de filtros aplicados
    const ribbonY = 23;
    doc.setFillColor(248, 250, 252); // Slate-50
    doc.setDrawColor(226, 232, 240); // Slate-200
    doc.setLineWidth(0.3);
    doc.roundedRect(marginLeft, ribbonY, contentWidth, 8, 1, 1, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(71, 85, 105);
    doc.text('Filtros:', marginLeft + 3, ribbonY + 5.2);

    doc.setFont('helvetica', 'normal');
    doc.text('Estado: ', marginLeft + 18, ribbonY + 5.2);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text(statusLabel, marginLeft + 28, ribbonY + 5.2);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text('Asesor: ', marginLeft + 80, ribbonY + 5.2);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text(agentLabel, marginLeft + 90, ribbonY + 5.2);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text('Período: ', marginLeft + 160, ribbonY + 5.2);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text(dateRangeLabel, marginLeft + 172, ribbonY + 5.2);
  };

  // Encabezado para páginas posteriores
  const drawSubsequentHeader = (pageNumber) => {
    doc.setFillColor(2, 132, 199);
    doc.rect(0, 0, pageWidth, 2.5, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(15, 23, 42);
    doc.text('Reporte de Tickets de Soporte — Akasia Telecomunicaciones', marginLeft, 10);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(100, 116, 139);
    doc.text(`Página ${pageNumber}`, pageWidth - marginRight, 10, { align: 'right' });
  };

  // Dibujar cabecera de la tabla
  const drawTableHeader = (startY) => {
    doc.setFillColor(30, 41, 59); // Slate-800 Navy
    doc.rect(marginLeft, startY, contentWidth, 7, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.2);
    doc.setTextColor(255, 255, 255);

    let x = marginLeft + 2;
    doc.text('# TICKET', x, startY + 4.6);
    x += colWidths.ticket;
    doc.text('FECHA', x, startY + 4.6);
    x += colWidths.fecha;
    doc.text('CLIENTE', x, startY + 4.6);
    x += colWidths.cliente;
    doc.text('TELÉFONO', x, startY + 4.6);
    x += colWidths.telefono;
    doc.text('ASUNTO', x, startY + 4.6);
    x += colWidths.asunto;
    doc.text('ASESOR ASIGNADO', x, startY + 4.6);
    x += colWidths.asesor;
    doc.text('ESTADO', x + colWidths.estatus / 2, startY + 4.6, { align: 'center' });
    x += colWidths.estatus;
    doc.text('1RA RESP.', x + colWidths.tiempo / 2, startY + 4.6, { align: 'center' });
  };

  // Pie de página corporativo
  const drawFooter = (pageNumber, totalPages) => {
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(marginLeft, pageHeight - 9, pageWidth - marginRight, pageHeight - 9);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);
    doc.text('Akasia Telecomunicaciones — Documento confidencial generado desde Support Engine', marginLeft, pageHeight - 5);
    doc.text(`Página ${pageNumber} de ${totalPages}`, pageWidth - marginRight, pageHeight - 5, { align: 'right' });
  };

  // Renderizar primera página
  drawMainHeader();
  let y = 35;
  drawTableHeader(y);
  y += 7;

  let currentPage = 1;
  const rowHeight = 7.2;
  const maxY = pageHeight - 14;

  tickets.forEach((ticket, idx) => {
    if (y + rowHeight > maxY) {
      doc.addPage();
      currentPage++;
      drawSubsequentHeader(currentPage);
      y = 15;
      drawTableHeader(y);
      y += 7;
    }

    // Fila cebra
    if (idx % 2 === 1) {
      doc.setFillColor(248, 250, 252); // Slate-50
      doc.rect(marginLeft, y, contentWidth, rowHeight, 'F');
    }

    // Línea inferior suave
    doc.setDrawColor(241, 245, 249);
    doc.setLineWidth(0.2);
    doc.line(marginLeft, y + rowHeight, marginLeft + contentWidth, y + rowHeight);

    let x = marginLeft + 2;

    // 1. # Ticket
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(2, 132, 199);
    doc.text(`#${ticket.idTicket}`, x, y + 4.6);
    x += colWidths.ticket;

    // 2. Fecha
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    const dateFormatted = ticket.dCreatedDate
      ? new Date(ticket.dCreatedDate).toLocaleDateString('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
      : '-';
    doc.text(dateFormatted, x, y + 4.6);
    x += colWidths.fecha;

    // 3. Cliente
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(15, 23, 42);
    const clientName = (ticket.vCustomerName || 'Sin cliente').length > 25
      ? (ticket.vCustomerName || 'Sin cliente').substring(0, 23) + '...'
      : (ticket.vCustomerName || 'Sin cliente');
    doc.text(clientName, x, y + 4.6);
    x += colWidths.cliente;

    // 4. Teléfono
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    const phone = ticket.vTelefono || '-';
    doc.text(phone, x, y + 4.6);
    x += colWidths.telefono;

    // 5. Asunto
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(51, 65, 85);
    const subject = (ticket.vAsunto || 'Sin asunto').length > 40
      ? (ticket.vAsunto || 'Sin asunto').substring(0, 38) + '...'
      : (ticket.vAsunto || 'Sin asunto');
    doc.text(subject, x, y + 4.6);
    x += colWidths.asunto;

    // 6. Asesor Asignado
    doc.setTextColor(71, 85, 105);
    const agent = (ticket.vNombreUsuarioAsignado || 'Sin asignar').length > 24
      ? (ticket.vNombreUsuarioAsignado || 'Sin asignar').substring(0, 22) + '...'
      : (ticket.vNombreUsuarioAsignado || 'Sin asignar');
    doc.text(agent, x, y + 4.6);
    x += colWidths.asesor;

    // 7. Estado (Badge estilizado con bordes redondeados)
    const badge = getStatusBadgeInfo(ticket);
    const badgeW = 20;
    const badgeH = 4.8;
    const badgeX = x + (colWidths.estatus - badgeW) / 2;
    const badgeY = y + 1.2;

    doc.setFillColor(badge.bg[0], badge.bg[1], badge.bg[2]);
    doc.setDrawColor(badge.border[0], badge.border[1], badge.border[2]);
    doc.setLineWidth(0.2);
    doc.roundedRect(badgeX, badgeY, badgeW, badgeH, 1, 1, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.setTextColor(badge.text[0], badge.text[1], badge.text[2]);
    doc.text(badge.label, badgeX + badgeW / 2, badgeY + 3.4, { align: 'center' });
    x += colWidths.estatus;

    // 8. Tiempo de respuesta
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    const respTime = ticket.iTiempoRespuestaMinutos != null
      ? (Number(ticket.iTiempoRespuestaMinutos) < 1 ? '< 1 min' : `${ticket.iTiempoRespuestaMinutos} min`)
      : '-';
    doc.text(respTime, x + colWidths.tiempo / 2, y + 4.6, { align: 'center' });

    y += rowHeight;
  });

  // Numerar páginas en el footer
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(i, totalPages);
  }

  // Descarga del PDF
  const dateStr = new Date().toISOString().slice(0, 10);
  doc.save(`Reporte_Tickets_${dateStr}.pdf`);
};
